import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { toast } from 'sonner'

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'https://api.varicash.com/api'

// In-memory refresh token — never written to localStorage
let _refreshToken: string | null = null
export const setRefreshToken = (token: string | null) => { _refreshToken = token }
export const getRefreshToken = () => _refreshToken

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true, // sends HTTP-only access_token cookie automatically
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            if (_refreshToken) {
              // Cookie-based auth: backend refreshes the access_token cookie server-side
              await axios.post(`${API_BASE_URL}/token/refresh/`, {
                refresh: _refreshToken,
              }, { withCredentials: true })

              return this.api(originalRequest)
            }
          } catch (refreshError) {
            setRefreshToken(null)
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        }

        // Handle other errors with proper type checking
        const status = error.response?.status
        const errorData = error.response?.data as any
        
        if (status && status >= 500) {
          toast.error('Server error. Please try again later.')
        } else if (status === 403) {
          toast.error('Access denied.')
        } else if (errorData?.message) {
          toast.error(errorData.message)
        } else if (errorData?.error) {
          toast.error(errorData.error)
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.get(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config)
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config)
  }
}

export const apiService = new ApiService()
export default apiService
